import React, { useState } from "react";
import {
    BAD_WEATHER,
    BOTH_GOOD_AND_BAD_WEATHER,
    COLORED_ROUTE,
    GOOD_WEATHER,
    SurfaceEnum,
    WATCHES_WARNINGS_ADVISORIES,
} from "../UserVisibleStrings";
import { Chip, FormControl, ListSubheader, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { displayName } from "../../app/forecastSurface";
import { MapStyle, useMapStyle } from "../../features/mapDisplay/mapDisplaySlice";
import { useIsRightBarExpanded } from "../../features/Drawer/drawerSlice";
import { PRETTY_BLUE } from "../trips/form/BorderWithLabel";
import SelectedSurfaceChip from "./SelectedSurfaceChip";
import { RouteViewState, useRouteViewState } from "../../features/riskViewer/riskViewerSlice";
import { ZIndexLevels } from "../risk/geoUtils";

const aggregateSurfaces = [
    BOTH_GOOD_AND_BAD_WEATHER,
    GOOD_WEATHER,
    BAD_WEATHER,
];

interface ImpactSurfaceDropdownProps
{
    selectSurface: ( surfaceName: string ) => void;
    selectedSurface: string;
    allSurfaces: Array<SurfaceEnum>;
}

const ImpactSurfaceDropdown: React.FC<ImpactSurfaceDropdownProps> = ( {
    selectSurface,
    selectedSurface,
    allSurfaces,
} ) =>
{
    // Map to string values directly
    const impactSurfaces = allSurfaces
        .filter( surface => surface !== SurfaceEnum.AGGREGATE )
        .map( surface => SurfaceEnum[surface] as string );

    const handleChange = ( event: SelectChangeEvent<string> ) =>
    {
        const surfaceName = event.target.value as string;
        selectSurface( surfaceName );
    };

    const mapStyle = useMapStyle();
    const routeViewState = useRouteViewState();
    const isDarkMode = ( mapStyle === MapStyle.Satellite || mapStyle === MapStyle.Dark );
    const isRightBarExpanded = useIsRightBarExpanded();
    const [ isOpen, setIsOpen ] = useState( false );
    const backgroundColor = PRETTY_BLUE;
    const [ isHovered, setIsHovered ] = useState( false );
    const backgroundColorHovered = "#e0e0e0";
    const shouldEnableColoredRoute =
        routeViewState === RouteViewState.ROUTE_WILMA ||
        routeViewState === RouteViewState.COLORED_ROUTE;

    const darkModeSelectStyles = {
        color: "white",
        ".MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(228, 219, 233, 0.25)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(228, 219, 233, 0.25)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(228, 219, 233, 0.25)",
        },
        ".MuiSvgIcon-root ": {
            fill: "white !important",
        },
        "& .MuiSelect-select": {
            paddingRight: "0px !important"
        }
    };

    const lightModeSelectStyles = {
        "& .MuiSelect-select": {
            paddingRight: "0px !important"
        }
    };

    return (
        <div style = {{ position: "absolute",
            right: isRightBarExpanded ? "315px" : "40px",
            transition: "top 0.3s ease," +
                " right 0.3s ease",
            zIndex: ZIndexLevels.UIElement }}
        >
            <FormControl variant = "standard" sx = {{ position: "absolute", top: "20px", right: "20px" }} id = "step6">
                <Select
                    labelId = "surface-select-label"
                    value = {selectedSurface}
                    onChange = {handleChange}
                    MenuProps = {{
                        PaperProps: {
                            sx: {
                                backgroundColor: isDarkMode ? "black" : "white",
                                color: isDarkMode ? "white" : "black",
                            },
                        },
                    }}
                    sx = {isDarkMode ? darkModeSelectStyles : lightModeSelectStyles}
                    inputProps = {{ IconComponent: () => null }}
                    onOpen = {() =>
                    {
                        setIsOpen( true );
                        setIsHovered( true );
                    }}
                    onClose = {() =>
                    {
                        setIsOpen( false );
                        setIsHovered( false );
                    }}
                    renderValue = {value => (
                        <SelectedSurfaceChip
                            value = {value}
                            isHovered = {isHovered}
                            isOpen = {isOpen}
                            backgroundColor = {backgroundColor}
                            backgroundColorHovered = {backgroundColorHovered}
                            setIsHovered = {setIsHovered}
                        />
                    )}
                    disableUnderline
                >
                    {aggregateSurfaces.map( surface => (
                        <MenuItem key = {surface} value = {surface} sx = {{ width: "100%" }}>
                            <Chip label = {displayName( surface )} sx = {{ width: "100%", backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.15)" }}/>
                        </MenuItem>
                    ) )}
                    <ListSubheader style = {{ backgroundColor: "transparent" }}>
                        <hr style = {{ margin: "10px 0", backgroundColor: "transparent" }}/>
                    </ListSubheader>
                    {impactSurfaces.map( surface => (
                        <MenuItem key = {surface} value = {surface} sx = {{ width: "100%" }}>
                            <Chip label = {displayName( surface )} sx = {{ width: "100%", backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.15)" }}/>
                        </MenuItem>
                    ) )}
                    <ListSubheader style = {{ backgroundColor: "transparent" }}>
                        <hr style = {{ margin: "10px 0", backgroundColor: "transparent" }}/>
                    </ListSubheader>
                    <MenuItem key = {WATCHES_WARNINGS_ADVISORIES} value = {WATCHES_WARNINGS_ADVISORIES} sx = {{ width: "100%" }}>
                        <Chip label = {displayName( WATCHES_WARNINGS_ADVISORIES )} sx = {{ width: "100%", backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.15)" }}/>
                    </MenuItem>
                    <ListSubheader style = {{ backgroundColor: "transparent" }}>
                        <hr style = {{ margin: "10px 0", backgroundColor: "transparent" }}/>
                    </ListSubheader>
                    <MenuItem key = {COLORED_ROUTE} disabled = {!shouldEnableColoredRoute} value = {COLORED_ROUTE} sx = {{ width: "100%" }}>
                        <Chip label = {displayName( COLORED_ROUTE )} sx = {{ width: "100%", backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.15)" }}/>
                    </MenuItem>
                </Select>
            </FormControl>
        </div>
    );
};

export default ImpactSurfaceDropdown;
